import type { ReactNode } from 'react'

type TileColor = 'hazard' | 'cobalt' | 'hot' | 'default' | 'cream' | 'teal'

interface ProjectCardProps {
	color: TileColor
	lab: string
	title: ReactNode
	body: string
	tags: string
	isLastCol?: boolean
	isLastMobile?: boolean
	hasBorderBottom?: boolean
	className?: string
}

const styles: Record<TileColor, { tile: string; lab: string; heading: string; body: string; tags: string }> = {
	hazard: {
		tile: 'bg-hazard text-ink',
		lab: 'bg-ink text-hazard',
		heading: 'text-ink',
		body: 'text-ink-2',
		tags: 'text-ink-dim',
	},
	cobalt: {
		tile: 'bg-cobalt text-paper',
		lab: 'bg-paper text-cobalt',
		heading: 'text-paper',
		body: 'text-paper',
		tags: 'text-paper',
	},
	hot: {
		tile: 'bg-hot text-paper',
		lab: 'bg-paper text-hot',
		heading: 'text-paper',
		body: 'text-paper',
		tags: 'text-paper',
	},
	default: {
		tile: 'bg-paper text-ink',
		lab: 'bg-ink text-paper',
		heading: 'text-ink',
		body: 'text-ink-2',
		tags: 'text-ink-dim',
	},
	cream: {
		tile: 'bg-paper-2 text-ink',
		lab: 'bg-ink text-paper',
		heading: 'text-ink',
		body: 'text-ink-2',
		tags: 'text-ink-dim',
	},
	teal: {
		tile: 'bg-teal text-ink',
		lab: 'bg-ink text-teal',
		heading: 'text-ink',
		body: 'text-ink',
		tags: 'text-ink',
	},
}

const ProjectCard = ({ color, lab, title, body, tags, isLastCol = false, isLastMobile = false, hasBorderBottom = false, className = '' }: ProjectCardProps) => {
	const s = styles[color]
	return (
		<div
			className={[
				s.tile,
				'p-[28px] min-h-[360px] flex flex-col gap-4',
				// Mobile: bottom border between stacked cards; desktop: right border between columns
				'border-b-2 border-ink md:border-b-0 md:border-r-2 md:border-ink',
				// Remove right border for last column on desktop
				isLastCol ? 'md:border-r-0' : '',
				// Show bottom border between rows on desktop (not shown by default)
				hasBorderBottom ? 'md:border-b-2' : '',
				// Remove bottom border for last item on mobile (avoids doubling with container border)
				isLastMobile ? 'border-b-0' : '',
				'transition-transform duration-200',
				'hover:-translate-x-[3px] hover:-translate-y-[3px]',
				'hover:shadow-[6px_6px_0_#0a0a08]',
				className,
			]
				.filter(Boolean)
				.join(' ')}
		>
			<span className={`${s.lab} font-mono text-[11px] uppercase px-[10px] py-[5px] self-start`}>
				{lab}
			</span>
			<h3 className={`${s.heading} font-display font-extrabold uppercase text-[32px] leading-[1.05]`}>
				{title}
			</h3>
			<p className={`${s.body} font-serif text-[15px] leading-[1.5] mt-auto`}>{body}</p>
			<span className={`${s.tags} font-mono text-[10px] uppercase mt-2`}>{tags}</span>
		</div>
	)
}

export default ProjectCard
